"use client";

import { Experience } from "@ninetailed/experience.js-react";
import { BaselineWithExperiencesEntry, Entry, ExperienceMapper } from "@ninetailed/experience.js-utils-contentful";
import cn from "@utils/cn";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { Mousewheel, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "@/components/HeroCarousel/HeroCarousel.css";
import DynamicImport from "@/components/dynamicImport";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import { parseCustomExperience } from "@/lib/experiences";

import ExperienceWrapperProps from "../ExperienceWrapperProps";
import { HomePageHeroCarouselProps } from "./types";

export type singularBlock = Entry | BaselineWithExperiencesEntry;
export type singularOrArrayBlock = singularBlock | Entry[] | BaselineWithExperiencesEntry[];

SwiperCore.use([Mousewheel, Pagination]);

const parseMenuItemExperiences = (item: any) => {
  if (item?.nt_experiences && Array.isArray(item.nt_experiences)) {
    // console.log("Raw nt_experiences for item:", item.id, item.nt_experiences);
    // Transform experiences to match Contentful structure
    const transformedExperiences = item.nt_experiences.map((experience: any) => ({
      sys: {
        id: experience.id,
        type: "Entry",
        contentType: {
          sys: {
            id: "nt_experience",
            type: "ContentType",
            linkType: "ContentType",
          },
        },
      },
      fields: {
        nt_name: experience.nt_name,
        nt_description: experience.nt_description,
        nt_type: experience.nt_type,
        nt_config: experience.nt_config,
        nt_audience: experience.nt_audience
          ? {
              sys: {
                id: experience.nt_audience.nt_audience_id || experience.nt_audience.id,
                type: "Entry",
                contentType: {
                  sys: {
                    id: "nt_audience",
                    type: "ContentType",
                    linkType: "ContentType",
                  },
                },
              },
              fields: {
                nt_name: experience.nt_audience.nt_name,
                nt_rules: experience.nt_audience.nt_rules,
                nt_audience_id: experience.nt_audience.nt_audience_id,
                nt_metadata: experience.nt_audience.nt_metadata,
              },
            }
          : undefined,
        nt_variants: experience.nt_variants
          ? experience.nt_variants.map((variant: any) => ({
              sys: {
                id: variant.id,
                type: "Entry",
                contentType: {
                  sys: {
                    id: variant.type || "menuItem",
                    type: "ContentType",
                    linkType: "ContentType",
                  },
                },
              },
              fields: {
                ...variant,
              },
            }))
          : undefined,
        nt_experience_id: experience.nt_experience_id,
        nt_metadata: experience.nt_metadata,
      },
    }));
    // console.log("Transformed experiences for item:", item.id, transformedExperiences);
    const mappedExperiences = transformedExperiences
      .map((experience: any) => {
        try {
          return ExperienceMapper.mapExperience(experience);
        } catch (error) {
          // console.error("Error mapping experience:", experience, error);
          // Fallback to raw experience if mapping fails
          return {
            id: experience.sys.id,
            name: experience.fields.nt_name,
            type: experience.fields.nt_type,
            config: experience.fields.nt_config,
            audience: experience.fields.nt_audience
              ? {
                  id: experience.fields.nt_audience.sys.id,
                  name: experience.fields.nt_audience.fields.nt_name,
                  rules: experience.fields.nt_audience.fields.nt_rules,
                }
              : undefined,
            variants: experience.fields.nt_variants
              ? experience.fields.nt_variants.map((variant: any) => ({
                  id: variant.sys.id,
                  ...variant.fields,
                }))
              : [],
            experienceId: experience.fields.nt_experience_id,
            metadata: experience.fields.nt_metadata,
          };
        }
      })
      .filter((exp: any) => exp !== null);
    // console.log("Parsed experiences for item:", item.id, mappedExperiences);
    return mappedExperiences;
  }
  // console.log("No nt_experiences for item:", item.id);
  return [];
};

const getSwiperBreakpoints = (isMultiSlide: boolean): any => {
  const multiSlideBreakpoints = {
    1024: {
      direction: "vertical",
      mousewheel: {
        enabled: true,
        eventsTarget: "#homepage-hero-carousel",
        thresholdDelta: 10,
      },
    },
  };
  return isMultiSlide ? multiSlideBreakpoints : {};
};

const HomePageHeroCarousel: FC<HomePageHeroCarouselProps> = ({ entry }) => {
  // console.log("HomePageHeroCarousel received entry:", entry);
  // console.log("Entry nt_experiences:", entry?.nt_experiences);

  const isSo = useIsSaleScreen();
  let { heroSlides } = entry;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const swiperRef = useRef<SwiperCore>();
  const container = useRef<HTMLDivElement>(null);
  const [isAfterVisible, setAfterVisible] = useState(false);

  const singleSlide = heroSlides.length === 1;

  const handleScroll = () => {
    if (entry.animation) {
      const heroHeight = window.innerHeight;
      const scrollPosition = window.scrollY || window.pageYOffset;

      if (scrollPosition >= heroHeight / (!isMobile ? 1.1 : 1.75)) {
        setAfterVisible(true);
      } else {
        setAfterVisible(false);
      }
    }
  };

  useEffect(() => {
    if (entry.animation) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (isDesktop && event.deltaY < 0 && !swiperRef.current?.mousewheel.enabled) {
      swiperRef.current?.mousewheel.enable();
    }
  };

  const getSiblings = (elm: any) => elm && elm.parentNode && [...elm.parentNode.children].filter((node) => node !== elm);

  useEffect(() => {
    if (container.current && entry.animation) {
      let siblings = getSiblings(container.current);

      siblings.forEach((sibling: HTMLElement) => {
        sibling.style.backgroundColor = "white";
        sibling.style.zIndex = "10";
      });

      if (siblings instanceof HTMLTemplateElement) {
        const templateContent = siblings.content;
        templateContent.querySelectorAll("div").forEach((divInTemplate) => {
          divInTemplate.style.backgroundColor = "white";
          divInTemplate.style.zIndex = "10";
        });
      }
    }
  }, [container.current, entry.animation]);

  // Parse experiences from entry.nt_experiences (memoised so Experience does not re-resolve variants on every render)
  const parsedExperiences = useMemo(() => parseCustomExperience(entry), [entry]);

  // console.log("Final parsed experiences for Experience component:", parsedExperiences);

  return (
    <Experience
      {...entry}
      key={`experience-${entry.id}`}
      id={entry.id as any}
      component={HomePageHeroCarouselInner as any}
      experiences={parsedExperiences}
      passthroughProps={{ isSo, isAfterVisible, isDesktop, onWheel: handleWheel, containerRef: container, swiperRef } as any}
    />
  );
};

interface InnerProps {
  fields?: any;
  heroSlides?: any[];
  animation?: boolean;
  isSo: boolean;
  isAfterVisible: boolean;
  isDesktop?: boolean;
  onWheel: (event: React.WheelEvent<HTMLDivElement>) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  swiperRef: React.MutableRefObject<SwiperCore | undefined>;
}

// Defined at module level so its identity is stable across parent renders.
// If it were declared inside HomePageHeroCarousel, every parent render (media queries, scroll state)
// would create a new component type and React would remount the Swiper and every slide, re-downloading hero videos.
const HomePageHeroCarouselInner = (props: InnerProps) => {
  const { isSo, isAfterVisible, isDesktop, onWheel, containerRef, swiperRef } = props;
  const activeEntry = props?.fields || props;

  let heroSlides: any[] = activeEntry?.heroSlides || [];

  if (isSo) {
    heroSlides = heroSlides.slice(0, 2);
  }

  const singleSlide = heroSlides.length === 1;

  return (
    <div
      className={cn("w-full h-svh", activeEntry.animation && "sticky top-0 left-0 !z-[1] is-sticky", isAfterVisible && "after-visible")}
      id="homepage-hero-carousel"
      onWheel={onWheel}
      ref={containerRef}
    >
      <Swiper
        className="w-full h-full"
        speed={1500}
        breakpoints={getSwiperBreakpoints(heroSlides.length > 1)}
        onSwiper={(swiper) => heroSlides.length > 1 && (swiperRef.current = swiper)}
        onTransitionStart={(swiper) => {
          if (!(swiper as SwiperCore).isEnd) {
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
          }
        }}
        onTransitionEnd={(swiper) => {
          if (isDesktop) {
            if (swiper.isEnd) {
              swiper.mousewheel.disable();
            }
          }
        }}
        simulateTouch={false}
        pagination={isDesktop ? false : { type: "bullets", clickable: true }}
      >
        {heroSlides.map((slide: any, i: number) => (
          <SwiperSlide key={i}>
            <DynamicImport components={[slide]} h1ID={i === 0 && slide.id} animation={activeEntry.animation} singleSlide={singleSlide} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default HomePageHeroCarousel;
